import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ElectionCategory } from '../../constants/categories';

export interface PollingScheduleItem {
  id: string;
  poll_date: string; // YYYY-MM-DD
  time_from: string; // HH:MM, 24-hour
  time_to: string;
  // De-duplicated, always in the order wardpanch, sarpanch, panchayat_samiti, zila_parishad
  categories: ElectionCategory[];
  label: string | null;
  // Inactive entries can't be picked for new wards; wards that use one keep it
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Wards in the app that use this entry: delete is blocked while > 0,
  // and a date/time change applies to all of them
  ward_count: number;
}

export interface PollingScheduleState {
  items: PollingScheduleItem[];
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
  error: string | null;
}

const initialState: PollingScheduleState = {
  items: [],
  total: 0,
  limit: 25,
  offset: 0,
  loading: false,
  error: null,
};

const pollingScheduleSlice = createSlice({
  name: 'pollingSchedules',
  initialState,
  reducers: {
    setPollingScheduleLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setPollingScheduleError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setPollingSchedulePage: (
      state,
      action: PayloadAction<{ items: PollingScheduleItem[]; total: number; limit: number; offset: number }>
    ) => {
      const { items, total, limit, offset } = action.payload;
      state.items = items;
      state.total = total;
      state.limit = limit;
      state.offset = offset;
    },
    updatePollingScheduleReducer: (state, action: PayloadAction<PollingScheduleItem>) => {
      const idx = state.items.findIndex((s) => s.id === action.payload.id);
      if (idx !== -1) state.items[idx] = action.payload;
    },
    deletePollingScheduleReducer: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((s) => s.id !== action.payload);
      state.total = Math.max(0, state.total - 1);
    },
  },
});

export const {
  setPollingScheduleLoading,
  setPollingScheduleError,
  setPollingSchedulePage,
  updatePollingScheduleReducer,
  deletePollingScheduleReducer,
} = pollingScheduleSlice.actions;

export default pollingScheduleSlice.reducer;
